<template>
  <div class="blip-editor">
    <blip-toolbar
      v-if="editor && editable"
      :editor="editor"
      :tokens="tokens"
      :image="image"
      :font-size="fontSize"
      :mode="mode"
      :upload-image="uploadImage"
      @set-mode="setMode"
      @format-source="formatSource"
    />
    <div class="blip-editor__panes" :class="{ 'is-split': mode === 'html' }">
      <!-- 위지윅(에디터) 모드: v-show로 마운트 유지 → 모드 전환 시 PM view 재생성 회피. -->
      <editor-content v-show="mode === 'wysiwyg'" class="blip-editor__content" :editor="editor" />
      <!-- HTML 모드: 좌=원시 HTML 직접 편집, 우=사용자 화면 렌더 미리보기. -->
      <template v-if="mode === 'html'">
        <textarea
          ref="source"
          class="blip-editor__source"
          :value="sourceText"
          :readonly="!editable"
          spellcheck="false"
          placeholder="<p>HTML 태그로 직접 작성할 수 있습니다</p>"
          @input="onSourceInput"
        />
        <!-- 미리보기: 입력한 HTML이 사용자 화면에 어떻게 보이는지 렌더(읽기 전용).
             내부 어드민이 작성한 신뢰 콘텐츠이며 동일 HTML이 앱에 그대로 노출되므로 v-html 사용. -->
        <div class="blip-editor__preview" v-html="previewHtml" />
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  shallowRef,
  triggerRef,
  markRaw,
  onMounted,
  onUnmounted,
  watch,
  type PropType,
} from "vue";
import { Editor, EditorContent } from "@tiptap/vue-2";
import Placeholder from "@tiptap/extension-placeholder";
import {
  buildExtensions,
  tokenizeHtml,
  detokenizeHtml,
  formatHtml,
  FUNDING_TOKENS,
} from "@space-oddity-inc/editor-core";
import { BlipTokenVue } from "./extension";
import BlipToolbar from "./Toolbar.vue";

const TOKEN_KEYS = FUNDING_TOKENS.map((t) => t.key);
type EditMode = "wysiwyg" | "html";

export default defineComponent({
  name: "BlipEditor",
  components: { EditorContent, BlipToolbar },
  props: {
    /** 본문 HTML(v-model). 토큰은 순수 `{@키}` 텍스트 형태(서버 계약형). */
    value: { type: String, default: "" },
    /** blipToken 노드/툴바 토큰 버튼 사용 여부. */
    tokens: { type: Boolean, default: true },
    /** 이미지 익스텐션/툴바 이미지 버튼 사용 여부. */
    image: { type: Boolean, default: true },
    /** 글자 크기 마크/툴바 글자 크기 선택 사용 여부. */
    fontSize: { type: Boolean, default: true },
    /** 빈 본문일 때 표시할 placeholder. */
    placeholder: { type: String, default: "" },
    /** 편집 가능 여부. false면 툴바 숨김 + 읽기 전용. */
    editable: { type: Boolean, default: true },
    /** 이미지 파일 → CDN URL 업로더(주입식). 미지정 시 이미지 버튼은 경고. */
    uploadImage: {
      type: Function as PropType<(file: File) => Promise<string>>,
      default: undefined,
    },
    /** 편집 방식(.sync). 부모가 제어하면 여러 인스턴스의 모드를 묶을 수 있음(i18n 탭 공유). 미지정 시 내부 자체 관리. */
    mode: {
      type: String as PropType<EditMode>,
      default: undefined,
    },
  },
  setup(props, { emit }) {
    // shallowRef: 거대한 Editor 타입을 Vue가 깊게 unwrap하지 않게 해
    // TS2589(excessively deep) 회피 + 내부 PM 상태 deep reactivity 방지.
    const editor = shallowRef<Editor | null>(null);

    // 편집 방식: "wysiwyg"(에디터) | "html"(직접 편집 + 미리보기) + 소스 버퍼(저장형 HTML).
    // props.mode가 주어지면 그 값으로 시작(부모 제어), 아니면 위지윅.
    const mode = ref<EditMode>(props.mode ?? "wysiwyg");
    const sourceText = ref("");
    // HTML 모드에서 textarea를 실제 편집했는지. 미편집이면 모드 복귀 시 에디터에 되쓰지
    // 않아(=spurious emit 차단) 손대지 않은 로케일이 "작업 중"으로 오표기되지 않게 한다.
    let sourceDirty = false;
    // 미리보기는 입력 중인 소스(저장형 HTML)를 그대로 렌더 → 사용자 화면 결과.
    const previewHtml = computed(() => sourceText.value);

    // 서버 계약형(순수 `{@키}`) ↔ 에디터 로드형(`<span data-blip-token>`) 변환.
    const toEditor = (html: string): string =>
      props.tokens ? tokenizeHtml(html, TOKEN_KEYS) : html;
    const toStored = (html: string): string =>
      props.tokens ? detokenizeHtml(html) : html;

    const emitHtml = (html: string): void => {
      emit("input", html);
      emit("update:value", html);
    };

    // 소스(textarea) → 에디터 반영(모드 전환 시). emitUpdate:false 로 onUpdate 루프 차단.
    const syncFromSource = (): void => {
      const ed = editor.value;
      if (!ed) return;
      // 소스 미편집이면 에디터는 이미 동일 내용 → 되쓰기/emit 생략(불필요한 dirty 방지).
      if (!sourceDirty) return;
      ed.commands.setContent(toEditor(sourceText.value), { emitUpdate: false });
      const html = toStored(ed.getHTML());
      // 에디터가 정규화한 HTML로 소스 버퍼·v-model 동기화.
      sourceText.value = html;
      sourceDirty = false;
      emitHtml(html);
    };

    // HTML 모드 타이핑: textarea가 권위 → 즉시 v-model 반영(미리보기는 computed로 자동).
    const onSourceInput = (e: Event): void => {
      sourceText.value = (e.target as HTMLTextAreaElement).value;
      sourceDirty = true;
      emitHtml(sourceText.value);
    };

    // 정렬 버튼(뷰티파이): 소스를 들여쓰기 정렬. textarea가 권위이므로 편집으로 간주(emit).
    const formatSource = (): void => {
      sourceText.value = formatHtml(sourceText.value);
      sourceDirty = true;
      emitHtml(sourceText.value);
    };

    const setMode = (next: EditMode): void => {
      const ed = editor.value;
      if (!ed || next === mode.value) return;
      if (next === "html") {
        // 에디터 현재 내용을 저장형 HTML로 소스에 시드 + 보기 좋게 정렬(표시용, emit 안 함).
        sourceText.value = formatHtml(toStored(ed.getHTML()));
        sourceDirty = false;
        mode.value = "html";
      } else {
        // 소스 편집분을 에디터에 반영한 뒤 위지윅으로 복귀.
        syncFromSource();
        mode.value = "wysiwyg";
      }
      // .sync: 부모가 여러 로케일 모드를 묶을 수 있게 변경을 위로 전파.
      emit("update:mode", mode.value);
    };

    onMounted(() => {
      const extensions = buildExtensions({
        image: props.image,
        tokens: false,
        fontSize: props.fontSize,
      });
      if (props.tokens) extensions.push(BlipTokenVue);
      if (props.placeholder) {
        extensions.push(Placeholder.configure({ placeholder: props.placeholder }));
      }

      editor.value = markRaw(
        new Editor({
          extensions,
          editable: props.editable,
          content: toEditor(props.value),
          onUpdate: ({ editor: ed }) => {
            // 위지윅 편집분만 onUpdate로 들어옴(HTML 모드에선 에디터를 건드리지 않음).
            emitHtml(toStored(ed.getHTML()));
          },
          onTransaction: () => {
            // shallowRef는 .value 재할당 시에만 트리거되므로 트랜잭션마다 강제 트리거 →
            // 툴바 isActive 상태 재계산.
            triggerRef(editor);
          },
        }),
      );
    });

    watch(
      () => props.value,
      (val) => {
        const ed = editor.value;
        if (!ed) return;
        // HTML 모드 중에는 textarea 가 권위 → 외부 value 변경으로 setContent 하지 않음.
        if (mode.value === "html") return;
        // onUpdate가 내보낸 값이 v-model로 되돌아온 경우(피드백 루프) skip.
        if (val === toStored(ed.getHTML())) return;
        ed.commands.setContent(toEditor(val), { emitUpdate: false });
      },
    );

    watch(
      () => props.editable,
      (val) => editor.value?.setEditable(val),
    );

    // .sync 외부 제어: 부모(i18n 래퍼)가 mode를 바꾸면 이 인스턴스도 따라 전환.
    // setMode 가드(next === mode.value)로 자기 emit→prop 되돌이 루프는 차단된다.
    watch(
      () => props.mode,
      (val) => {
        if (val && val !== mode.value) setMode(val);
      },
    );

    // destroyed(=onUnmounted)에서 파괴: 자식 <EditorContent>의 beforeDestroy가
    // editor.view에 무조건 접근하므로(EditorContent.ts), 자식 teardown 이후에
    // 파괴해야 "view not available" throw를 피한다.
    onUnmounted(() => {
      editor.value?.destroy();
    });

    return { editor, mode, sourceText, previewHtml, setMode, onSourceInput, formatSource };
  },
});
</script>

<style scoped>
.blip-editor {
  border-radius: 6px;
}
.blip-editor__panes {
  display: flex;
}
.blip-editor__panes > * {
  flex: 1 1 50%;
  min-width: 0;
}
.blip-editor__content {
  border: 1px solid #e5e7eb;
  border-radius: 0 0 6px 6px;
  padding: 12px 14px;
  min-height: 160px;
}
/* HTML 모드: 좌(소스)·우(미리보기) 2분할. */
.blip-editor__source {
  border: 1px solid #e5e7eb;
  border-radius: 0 0 0 6px;
  padding: 12px 14px;
  min-height: 160px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;
  color: #374151;
  background: #fbfbfd;
  resize: vertical;
  outline: none;
}
.blip-editor__preview {
  border: 1px solid #e5e7eb;
  border-left: 0;
  border-radius: 0 0 6px 0;
  padding: 12px 14px;
  min-height: 160px;
  overflow: auto;
  background: #fff;
  color: #111827;
  font-size: 14px;
  line-height: 1.6;
}
.blip-editor__preview img {
  max-width: 100%;
  height: auto;
}
.blip-editor__preview > :first-child {
  margin-top: 0;
}
.blip-editor__preview > :last-child {
  margin-bottom: 0;
}
.blip-editor__content :deep(.ProseMirror) {
  outline: none;
  min-height: 136px;
}
.blip-editor__content :deep(.ProseMirror p.is-editor-empty:first-child::before) {
  content: attr(data-placeholder);
  float: left;
  height: 0;
  color: #9ca3af;
  pointer-events: none;
}
</style>
